(ns superadmin.users.create.ocp
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Stack$default" :as Stack]
            ["react-hook-form" :as rhf]
            ["react-use/lib/useDebounce$default" :as useDebounce]
            [cljs-bean.core :refer [->clj]]
            [common.form :as form]
            [common.ocp.view :refer [defocp ocp]]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.agency :as agency-query]
            [common.widgets.form-field :refer [form-field]]
            [re-frame.core :as rf]
            [superadmin.roles.queries :as roles-query]
            [superadmin.users.queries :as q]
            [uix.core :as uix :refer [$ defui]]))

(defui ocp-footer [{:keys [^js formState control]}]
  (let [is-submitting (.-isSubmitting formState)
        is-valid (.-isValid formState)
        role-watch (rhf/useWatch #js {:control control
                                      :name "role"})
        role-clj (when role-watch (->clj role-watch))
        role-data (:value role-clj)
        role-scope (-> role-data :allowed-scopes first)
        agency-watch (rhf/useWatch #js {:control control
                                        :name "agency"})
        agency-clj (when agency-watch (->clj agency-watch))
        advertiser-watch (rhf/useWatch #js {:control control
                                            :name "advertiser"})
        advertiser-clj (when advertiser-watch (->clj advertiser-watch))
        scope-ok (cond
                   (= role-scope "agency") (some? (:value agency-clj))
                   (= role-scope "advertiser") (and (:value agency-clj)
                                                    (:value advertiser-clj))
                   :else true)
        disabled? (or (not is-valid) is-submitting (not scope-ok))]
    ($ Button
       {:type "submit"
        :variant "contained"
        :disabled disabled?
        :data-testid "user-create-submit"}
       (if is-submitting "Creating..." "Create User"))))

(defui user-create-form []
  (let [{:keys [control handleSubmit formState]}
        (form/use-form #js {:mode "onChange"
                            :defaultValues #js {:name ""
                                                :email ""
                                                :role nil
                                                :agency nil
                                                :advertiser nil}})

        {:keys [data isPending]} (use-query (roles-query/roles-query))
        role-options (uix/use-memo
                      (fn []
                        (map (fn [role]
                               {:label (:name role)
                                :value role})
                             (or data [])))
                      [data])

        agencies-query (use-query (agency-query/superadmin-agencies-query))
        agency-options (uix/use-memo
                        (fn []
                          (map (fn [agency]
                                 {:label (:name agency)
                                  :value (:id agency)})
                               (:data agencies-query)))
                        [agencies-query])

        role-watch (rhf/useWatch #js {:control control
                                      :name "role"})
        role (when role-watch (->clj role-watch))
        role-scope (-> role :allowed-scopes first)
        agency-watch (rhf/useWatch #js {:control control
                                        :name "agency"})
        agency (when agency-watch (->clj agency-watch))
        {advertisers-data :data} (use-query (q/agency-advertisers-query agency))
        advertiser-options (uix/use-memo
                            (fn []
                              (map (fn [advertiser]
                                     {:label (:name advertiser)
                                      :value (:id advertiser)})
                                   advertisers-data))
                            [advertisers-data])

        [email-input set-email-input] (uix/use-state "")
        [wpp-os-options set-wpp-os-options] (uix/use-state [])
        [wpp-os-loading set-wpp-os-loading] (uix/use-state false)

        _ (useDebounce (fn []
                         (if (and (seq email-input) (>= (count email-input) 3))
                           (do
                             (set-wpp-os-loading true)
                             (-> (q/wpp-os-search-users email-input)
                                 (.then (fn [result]
                                          (let [{:keys [data]} (->clj result)]
                                            (set-wpp-os-options
                                             (mapv (fn [user]
                                                     {:label (:email user)
                                                      :value (:email user)})
                                                   data)))))
                                 (.finally (fn [] (set-wpp-os-loading false)))))
                           (set-wpp-os-options [])))
                       1000
                       #js [email-input])

        {:keys [mutate]} (use-mutation (q/create-user-mutation))

        on-submit (uix/use-callback
                   (fn [data]
                     (let [{:keys [name email role agency advertiser]} (->clj data)
                           role-id (:id role)
                           role-scope (-> role :allowed-scopes first)
                           scope-id (cond
                                      (= role-scope "agency") agency
                                      (= role-scope "advertiser") advertiser
                                      :else nil)
                           base-payload (cond-> {:email email}
                                          (some? name) (assoc :name name))
                           grants (cond
                                    (and role-id scope-id role-scope)
                                    [{:role-id role-id
                                      :scope-type role-scope
                                      :scope-id scope-id}]
                                    :else
                                    [])
                           payload (assoc base-payload :grants grants)]
                       (mutate payload
                               #js {:onSuccess (fn []
                                                 (rf/dispatch [:ocp/close]))})))
                   [mutate])]
    ($ ocp
       {:title "Create User"
        :footer ($ ocp-footer {:formState formState
                               :control control})
        :on-submit (handleSubmit on-submit)}
       ($ Stack {:spacing 3}
          ($ form-field
             {:label "Name"
              :data-testid :user-create-ocp--name}
             ($ form/form-text-field
                {:name "name"
                 :control control
                 :rules #js {:required "This field is required"
                             :maxLength #js {:value 255
                                             :message "Name must be 255 characters or less"}}
                 :controller-props #js {:placeholder "e.g., Jane Doe"
                                        :data-testid "user-name-input"}}))
          ($ form-field
             {:label "Email"
              :data-testid :user-create-ocp--email}
             ($ form/autocomplete-field
                {:name "email"
                 :control control
                 :rules #js {:required "This field is required"}
                 :field-props {:options wpp-os-options
                               :loading wpp-os-loading
                               :open-on-focus? false
                               :clearable? true
                               :placeholder "Search WPP OS users by email"
                               :filter-options (fn [options _state] options)
                               :onInputChange (fn [_event value reason]
                                                (when (= reason "input")
                                                  (set-email-input value)))}
                 :text-field-props {:data-testid "user-email-input"}}))
          ($ form-field
             {:label "Role"
              :data-testid :user-create-ocp--role}
             ($ form/autocomplete-field
                {:name "role"
                 :control control
                 :rules #js {:required "This field is required"}
                 :field-props {:options role-options
                               :loading isPending}}))

          (when (or (= role-scope "agency") (= role-scope "advertiser"))
            ($ form-field
               {:label "Agency"}
               ($ form/autocomplete-field
                  {:name "agency"
                   :control control
                   :field-props {:options agency-options}})))

          (when (= role-scope "advertiser")
            ($ form-field
               {:label "Advertiser"}
               ($ form/autocomplete-field
                  {:name "advertiser"
                   :control control
                   :field-props {:options advertiser-options}})))))))

(defocp user-create-ocp :superadmin/user-create [_]
  ($ user-create-form))
